/**
 * P3-27: RacePanel —— Trade tab 单种族面板。
 *
 * 迁移自 `classes.diplomacy.ui.RacePanel`（js/diplomacy.js:946-985）。
 *
 * 结构：折叠 Panel，标题含 attitude；内容分左右两列。
 * 左列：buys、sells（含 embassy 过滤与季节系数）、zebra titanium 特例。
 * 右列：TradeButton、EmbassyButton（非 leviathans）、autoPinnedButton（leviathans）、
 *      elders feed/bcoin（leviathans，P3-28）。
 */
import { useCallback, useMemo } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { AutoPinnedButton } from "./AutoPinnedButton";
import { EldersButtons } from "./EldersButtons";
import { EmbassyButton } from "./EmbassyButton";
import { Panel } from "../../components/Panel";
import { TradeButton } from "./TradeButton";
import type { RaceLike } from "./useTradeModel";

interface RacePanelProps {
  raceName: string;
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

interface RaceSnapshot {
  title: string;
  buysTitle: string;
  buysVal: number;
  sells: {
    title: string;
    min: string;
    max: string;
    isFirst: boolean;
  }[];
  zebraTitanium?: { title: string; val: string };
}

function computeRaceSnapshot(
  game: GamePage | null,
  raceName: string,
): RaceSnapshot | null {
  if (!game) {
    return null;
  }
  const dip = game.diplomacy as {
    get: (name: string) => RaceLike;
    isValidTrade: (s: RaceLike["sells"][0], race: RaceLike) => boolean;
    getFinalStanding: (race: RaceLike) => number;
    calculateTradeBonusFromPolicies: (raceName: string, game: GamePage) => number;
  };
  const race = dip.get(raceName);
  if (!race?.unlocked) {
    return null;
  }

  const finalStanding = dip.getFinalStanding(race);
  const attitude =
    race.standing > 0
      ? "friendly"
      : race.standing === 0
        ? finalStanding > 0
          ? "nowFriendly"
          : "neutral"
        : finalStanding < 0
          ? "hostile"
          : "nowNeutral";
  const title =
    race.title + " <span class='attitude'>" + t("trade.attitude." + attitude) + "</span>";

  const resPool = game.resPool as { get: (name: string) => { title?: string; name: string } };
  const buyRes = resPool.get(race.buys[0].name);
  const buysTitle = buyRes.title || buyRes.name;

  const baseTradeRatio =
    1 +
    (game.diplomacy as { getTradeRatio: () => number }).getTradeRatio() +
    dip.calculateTradeBonusFromPolicies(race.name, game) +
    (game.challenges as { getChallenge: (name: string) => { getTradeBonusEffect: (game: GamePage) => number } }).getChallenge("pacifism")
      .getTradeBonusEffect(game);
  const currentSeason = (game.calendar as { getCurSeason: () => { name: string } }).getCurSeason().name;
  const raceRatio = 1 + (race.energy ?? 0) * 0.02;

  const sells: RaceSnapshot["sells"] = [];
  for (const s of race.sells) {
    if (!dip.isValidTrade(s, race)) {
      continue;
    }
    const res = resPool.get(s.name);
    const average =
      s.value * baseTradeRatio * raceRatio * (1 + (s.seasons ? s.seasons[currentSeason] : 0));
    sells.push({
      title: res.title || res.name,
      min: game.getDisplayValueExt(average * (1 - s.width / 2), false, false, 0),
      max: game.getDisplayValueExt(average * (1 + s.width / 2), false, false, 0),
      isFirst: sells.length === 0,
    });
  }

  let zebraTitanium: RaceSnapshot["zebraTitanium"] = undefined;
  if (race.name === "zebras") {
    const titanium = resPool.get("titanium");
    const zebraRelationModifierTitanium =
      (game.getEffect("zebraRelationModifier") as number) *
      ((game.bld as { getBuildingExt: (name: string) => { meta?: { effects?: Record<string, number> } } }).getBuildingExt("tradepost").meta
        ?.effects?.["tradeRatio"] ?? 0);
    const displayedVal = game.getDisplayValueExt(
      (1.5 + (game.resPool as { get: (n: string) => { value: number } }).get("ship").value * 0.03) *
        (1 + zebraRelationModifierTitanium),
      false,
      false,
      0,
    );
    zebraTitanium = { title: titanium.title || titanium.name, val: displayedVal };
  }

  return {
    title,
    buysTitle,
    buysVal: race.buys[0].val,
    sells,
    zebraTitanium,
  };
}

function snapshotEq(a: RaceSnapshot | null, b: RaceSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.title !== b.title ||
    a.buysTitle !== b.buysTitle ||
    a.buysVal !== b.buysVal ||
    a.sells.length !== b.sells.length ||
    a.zebraTitanium?.title !== b.zebraTitanium?.title ||
    a.zebraTitanium?.val !== b.zebraTitanium?.val
  ) {
    return false;
  }
  for (let i = 0; i < a.sells.length; i++) {
    if (
      a.sells[i].title !== b.sells[i].title ||
      a.sells[i].min !== b.sells[i].min ||
      a.sells[i].max !== b.sells[i].max ||
      a.sells[i].isFirst !== b.sells[i].isFirst
    ) {
      return false;
    }
  }
  return true;
}

export function RacePanel({ raceName, collapsed, onToggle }: RacePanelProps) {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeRaceSnapshot(game, raceName), [raceName]),
    snapshotEq,
  );

  const leftColumn = useMemo(() => {
    if (!snap) {
      return null;
    }
    return (
      <div className="left">
        <div>
          <span className="buys">{t("trade.buys")}: </span>
          {snap.buysTitle} <span className="tradeAmount">{snap.buysVal}</span>
        </div>
        {snap.sells.map((s) => (
          <div key={s.title}>
            <span className="sells">{s.isFirst ? t("trade.sells") + ": " : ""}</span>
            {s.title}{" "}
            <span className="tradeAmount">
              {s.min} - {s.max}
            </span>
          </div>
        ))}
        {snap.zebraTitanium && (
          <div>
            <span className="sells"></span>
            {snap.zebraTitanium.title}{" "}
            <span className="tradeAmount">
              {snap.zebraTitanium.val} - {snap.zebraTitanium.val}
            </span>
          </div>
        )}
      </div>
    );
  }, [snap]);

  if (!snap) {
    return null;
  }

  return (
    <Panel title={snap.title} collapsed={collapsed} onToggle={onToggle}>
      <div className="trade-race">
        {leftColumn}
        <div className="right">
          <TradeButton raceName={raceName} />
          {raceName === "leviathans" ? (
            <>
              <EldersButtons />
              <AutoPinnedButton raceName={raceName} />
            </>
          ) : (
            <EmbassyButton raceName={raceName} />
          )}
        </div>
        <div className="clear" />
      </div>
    </Panel>
  );
}
